import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import extend from 'lodash/extend'
import { fetchCartId } from '../cartId'
import { Slice, CartSlice } from './types'
import { PWAThunk } from 'lib/core/data'

export const initialState: Slice = {}

interface ActionData {
  cartId: string
  data?: any
  merge?: boolean
}

let failTimes = 0
const maxFails = 3

const getSlice = (slice: CartSlice): CartSlice => {
  return slice ?? ({
    isFetching: false,
    isSuccess: false,
    isFailure: false,
    isCartException: false,
    data: undefined
  })
}

const slice = createSlice({
  name: 'quote/cart',
  initialState,
  reducers: {
    request (state, action: PayloadAction<ActionData>) {
      const { cartId } = action.payload
      const slice = getSlice(state[cartId])
      slice.isFetching = true
      state[cartId] = slice
    },

    success (state, action: PayloadAction<ActionData>) {
      failTimes = 0
      const { cartId, data } = action.payload
      const slice = getSlice(state[cartId])
      slice.isFetching = false
      slice.isSuccess = true
      slice.isFailure = false
      slice.isCartException = false
      slice.data = data
    },

    cartException (state, action: PayloadAction<ActionData>) {
      const { cartId } = action.payload
      const slice = getSlice(state[cartId])
      slice.isCartException = true
    },

    failure (state, action: PayloadAction<ActionData>) {
      const { cartId } = action.payload
      const slice = getSlice(state[cartId])
      slice.isFetching = false
      slice.isSuccess = false
      slice.isFailure = true
    },

    setCart (state, action: PayloadAction<ActionData>) {
      const { cartId, data, merge } = action.payload
      const slice = getSlice(state[cartId])
      slice.isFetching = false
      slice.isSuccess = true
      slice.isFailure = false
      slice.data = merge ? extend(slice.data, data) : data
    },

    completeCart (state, action: PayloadAction<ActionData>) {
      const { cartId } = action.payload
      const slice = getSlice(state[cartId])
      if (slice.data) {
        slice.data.done = true
      }
    }
  }
})

export const reducer = slice.reducer

export const { request, success, failure, setCart, completeCart, cartException } = slice.actions

export function fetchCart (cartId: string): PWAThunk {
  return async (dispatch, getState, params) => {
    const handleCartException = () => {
      if (failTimes <= maxFails - 1) {
        dispatch(fetchCartId(true))
      } else {
        dispatch(cartException({ cartId }))
      }
    }

    const { magentoClient } = params
    dispatch(request({ cartId }))
    try {
      const response = await magentoClient.getClient().quote.fetchCart({ cartId })
      const { data } = response
      const cart = data?.cart
      if (cart) {
        dispatch(success({ cartId, data: cart }))
      } else {
        failTimes++
        dispatch(failure({ cartId }))
        console.error('Cart failed. Payload:', data) // eslint-disable-line
        handleCartException()
      }
      return response
    } catch (e) {
      failTimes++
      dispatch(failure({ cartId }))
      console.error('Cart Exception. Payload:', e) // eslint-disable-line
      handleCartException()
    }
  }
}
